import reading from './hogeHuisPlan.json';
import type { BuildContext } from '../../types';
import type { PolygonIndex } from '../../../geo/polygonIndex';
import type { Vec2 } from '../../../geo/types';
import { difference, inflate, intersection } from '../../../geo/clip';
import { polygonArea } from '../../../geo/polygon';
import { footprintInside, lotsOverlap, obliqueCorners } from '../lotFootprint';
import { obbFromFrontEdge } from '../lotObb';
import { measureLine } from '../measuredLine';
import type { Lot, ReservedFootprint, RoofMaterial } from '../types';

export const HOGE_HUIS = reading;

/** The documented group takes priority over anonymous houses, without changing the bridgehead. */
export function planHogeHuis(
  ctx: Pick<BuildContext, 'layout' | 'site'>,
  buildable: PolygonIndex,
  reserved: readonly ReservedFootprint[]
) {
  const source = ctx.layout.streets.find((s) => s.id === reading.source);
  if (!source || !ctx.layout.landmarks.some((l) => l.id === 'landmark-nieuwe-brug-west-tower'))
    return { lots: [], reserved: [], unresolved: [] };
  const line = measureLine(
    source.centerline,
    source.centerline.map(() => source.width)
  );
  const makeLot = (
    house: (typeof reading.houses)[number] | (typeof reading.adjoiningFrontage.houses)[number],
    index: number,
    row: string
  ): Lot => {
    const { point, tangent } = line.pointAt(house.chainage);
    const inward: Vec2 = [tangent[1], -tangent[0]],
      facing: Vec2 = [-inward[0], -inward[1]],
      front: Vec2 = [
        point[0] + inward[0] * (source.width / 2 + 0.05),
        point[1] + inward[1] * (source.width / 2 + 0.05),
      ];
    const corners = obliqueCorners(front, facing, house.width, house.depth, 0);
    return {
      index,
      sourceId: source.id,
      side: 'right',
      ordinal: 56_000 + index,
      kind: 'house',
      facing,
      frontage: house.width,
      depth: house.depth,
      corners,
      obb: obbFromFrontEdge(corners[0], corners[1], house.depth, facing),
      reconstruction: {
        id: house.id,
        row,
        evidence: 'inferred',
        params: {
          storeys: house.storeys,
          storeyHeight: house.storeyHeight,
          pitch: Math.atan((2 * house.roofRise) / house.width),
          roofMaterial: house.roofMaterial as RoofMaterial,
          frontType: 'brick',
          frontColour: 'light',
          frontGable: 'plain',
          backGable: 'plain',
          chimneys: [{ at: 0.65, form: 'stack' }],
        },
      },
    };
  };
  const lots = reading.houses.map((house, index) => makeLot(house, index, reading.id));
  if (
    reading.houses.some((h) => h.chainage > line.length) ||
    lots.some(
      (lot, i) =>
        !footprintInside(buildable, lot.corners) ||
        lots.slice(0, i).some((other) => lotsOverlap(lot, other, 0.001)) ||
        intersection(
          [{ outer: [...lot.corners], holes: [] }],
          [...ctx.site.streets, ...ctx.site.water, ...reserved.map((r) => r.polygon)]
        ).some((p) => polygonArea(p) > 0.001)
    )
  )
    return { lots: [], reserved: [], unresolved: reading.houses.map((h) => h.id) };
  const unresolved: string[] = [];
  for (const [index, house] of reading.adjoiningFrontage.houses.entries()) {
    const lot = makeLot(house, reading.houses.length + index, reading.adjoiningFrontage.id);
    if (
      house.chainage > line.length ||
      !footprintInside(buildable, lot.corners) ||
      lots.some((other) => lotsOverlap(lot, other, 0.001)) ||
      intersection(
        [{ outer: [...lot.corners], holes: [] }],
        [...ctx.site.streets, ...ctx.site.water, ...reserved.map((r) => r.polygon)]
      ).some((p) => polygonArea(p) > 0.001)
    )
      unresolved.push(house.id);
    else lots.push(lot);
  }
  const footprints = lots.map((lot): ReservedFootprint => ({
    id: `surveyed:${lot.reconstruction!.id}`,
    polygon: { outer: [...lot.corners], holes: [] },
  }));
  const yard = intersection(
    [{ outer: reading.privateGround.map((p) => [p[0]!, p[1]!] as Vec2), holes: [] }],
    ctx.site.buildable
  );
  const dry = difference(yard, [
    ...ctx.site.streets,
    ...ctx.site.water,
    ...reserved.map((r) => r.polygon),
    // Separate quantized yard cuts from the unrounded house wall by one centimetre.
    ...inflate(
      footprints.map((r) => r.polygon),
      0.01,
      'miter'
    ),
  ]);
  return {
    lots,
    reserved: [
      ...footprints,
      ...dry.map((polygon, i) => ({ id: `hoge-huis-timber-yard-${i}`, polygon })),
    ],
    unresolved,
  };
}
